import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ShieldCheck, Lock, CreditCard, Star, Users, BookOpen, MapPin } from 'lucide-react';
export function HeroSection() {
  return <section className="relative bg-hero-pattern text-white overflow-hidden pt-10 pb-14 lg:pt-24 lg:pb-32" data-id="element-156">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0" data-id="element-157">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-yoga-purple-600/30 blur-[120px]" data-id="element-158"></div>
        <div className="absolute top-[40%] -right-[10%] w-[40%] h-[60%] rounded-full bg-yoga-gold/20 blur-[150px]" data-id="element-159"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10" data-id="element-160">
        <motion.div className="flex flex-col items-center text-center space-y-5 md:space-y-8 max-w-4xl mx-auto" initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} data-id="element-161">
          <div className="inline-block bg-yoga-purple-800/50 border border-yoga-gold/30 rounded-full px-3 py-1 text-xs md:text-sm font-medium text-yoga-gold-300 w-fit backdrop-blur-sm" data-id="element-162">
            Sistema completo para instructoras
          </div>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight" data-id="element-163">
            Todo tu año de{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yoga-gold-300 to-yoga-gold-500" data-id="element-164">
              Yoga Resuelta.
            </span>
          </h1>

          {/* Product Image - 70% width, between title and subtitle */}
          <motion.div className="w-[60%] md:w-[70%] max-w-2xl mx-auto relative" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-165">
            <div className="absolute inset-0 bg-yoga-gold/15 rounded-full blur-[80px] transform scale-75" data-id="element-166"></div>
            <img src="https://cdn.magicpatterns.com/uploads/vh7aFgvfBEr5XmP8mDqX8H/heylloyd-image-1777599670400-Photoroom.png" alt="Megapack Yoga Resuelta" className="relative z-10 w-full h-auto object-contain drop-shadow-2xl" data-id="element-167" />
          </motion.div>

          <p className="md:text-2xl font-light text-yoga-purple-100 text-[16px]" data-id="element-168">
            6 Tomos · 60+ Clases · 6 Bonos · Listo para usar.
          </p>

          <p className="text-base md:text-lg text-yoga-purple-200 leading-relaxed max-w-2xl" data-id="element-169">
            Tu año completo de Yoga listo: clases, posturas, secuencias,
            poblaciones especiales, gestión y crecimiento profesional.{' '}
            <strong className="text-white font-semibold" data-id="element-170">
              Sin improvisar. Sin el domingo de pánico.
            </strong>
          </p>

          <div className="flex flex-wrap justify-center gap-2 md:gap-3" data-id="element-171">
            <span className="flex items-center gap-1.5 bg-white/10 px-2.5 py-1 md:px-3 md:py-1.5 rounded-md text-xs md:text-sm" data-id="element-172">
              <CheckCircle2 size={14} className="text-yoga-gold-400" data-id="element-173" /> PDF
              digital
            </span>
            <span className="flex items-center gap-1.5 bg-white/10 px-2.5 py-1 md:px-3 md:py-1.5 rounded-md text-xs md:text-sm" data-id="element-174">
              <CheckCircle2 size={14} className="text-yoga-gold-400" data-id="element-175" /> Acceso
              inmediato
            </span>
            <span className="flex items-center gap-1.5 bg-white/10 px-2.5 py-1 md:px-3 md:py-1.5 rounded-md text-xs md:text-sm" data-id="element-176">
              <CheckCircle2 size={14} className="text-yoga-gold-400" data-id="element-177" /> Todos
              los niveles
            </span>
          </div>

          <div className="pt-2 md:pt-4 flex flex-col items-center space-y-3 md:space-y-4" data-id="element-178">
            <motion.button whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="w-full sm:w-auto bg-gradient-to-r from-yoga-gold-500 to-yoga-gold-600 hover:from-yoga-gold-400 hover:to-yoga-gold-500 text-yoga-purple-950 font-bold md:text-xl py-3 md:py-4 px-6 md:px-8 rounded-xl shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all flex items-center justify-center gap-2 text-[14px]" data-id="element-179">
              <span className="text-xl md:text-2xl" data-id="element-180">💥</span> QUIERO MI YOGA
              RESUELTA AHORA
            </motion.button>

            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4 text-xs md:text-sm text-yoga-purple-200" data-id="element-181">
              <span className="flex items-center gap-1" data-id="element-182">
                <ShieldCheck size={14} data-id="element-183" /> Garantía 7 días
              </span>
              <span className="flex items-center gap-1" data-id="element-184">
                <Lock size={14} data-id="element-185" /> Pago 100% seguro
              </span>
              <span className="flex items-center gap-1" data-id="element-186">
                <CreditCard size={14} data-id="element-187" /> Mercado Pago | Tarjeta
              </span>
            </div>
          </div>
        </motion.div>

        {/* Stats Bar */}
        <motion.div className="mt-10 lg:mt-24 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-8 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 md:p-8" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.4
      }} data-id="element-188">
          <div className="flex flex-col items-center text-center space-y-1 md:space-y-2" data-id="element-189">
            <Users className="text-yoga-gold-400" size={22} data-id="element-190" />
            <span className="text-xl md:text-3xl font-bold text-white" data-id="element-191">
              +500
            </span>
            <span className="text-[10px] md:text-sm text-yoga-purple-200 uppercase tracking-wider" data-id="element-192">
              Profes Confían
            </span>
          </div>
          <div className="flex flex-col items-center text-center space-y-1 md:space-y-2" data-id="element-193">
            <BookOpen className="text-yoga-gold-400" size={22} data-id="element-194" />
            <span className="text-xl md:text-3xl font-bold text-white" data-id="element-195">
              6+6
            </span>
            <span className="text-[10px] md:text-sm text-yoga-purple-200 uppercase tracking-wider" data-id="element-196">
              Tomos + Bonos
            </span>
          </div>
          <div className="flex flex-col items-center text-center space-y-1 md:space-y-2" data-id="element-197">
            <Star className="text-yoga-gold-400" size={22} data-id="element-198" />
            <span className="text-xl md:text-3xl font-bold text-white" data-id="element-199">
              4.9/5
            </span>
            <span className="text-[10px] md:text-sm text-yoga-purple-200 uppercase tracking-wider" data-id="element-200">
              Calificación
            </span>
          </div>
          <div className="flex flex-col items-center text-center space-y-1 md:space-y-2" data-id="element-201">
            <MapPin className="text-yoga-gold-400" size={22} data-id="element-202" />
            <span className="text-xl md:text-3xl font-bold text-white" data-id="element-203">🇦🇷</span>
            <span className="text-[10px] md:text-sm text-yoga-purple-200 uppercase tracking-wider" data-id="element-204">
              Todo el país
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}